import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LicenseModal from './LicenseModal';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useAuth } from '../../context/AuthContext';
import withPermission from '../../utils/withPermission';
import Swal from 'sweetalert2';
import { Offcanvas, Button, Form } from 'react-bootstrap';

const LicenseList = ({tenantId}) => {
    const [licenses, setLicenses] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedLicenseId, setSelectedLicenseId] = useState(null);
    const [selectedLicenseData, setSelectedLicenseData] = useState(null);
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    // const { id: tenantId } = useParams(); // Extract tenant_info_id from URL
    const didFetchRef = useRef(false);
    const [showMessage, setShowMessage] = useState(false);  // Controls visibility of the popup

    const [errors, setErrors] = useState({});
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [extendValue, setExtendValue] = useState(1);
    const [extendType, setExtendType] = useState('days');
    const navigate = useNavigate();
    const [extendedMessage, setExtendedMessage] = useState('');
    const [labelClicked, setLabelClicked] = useState(false);
    const WithEditPermission = withPermission(({ children }) => children, ['license-edit']);
    const WithDeletePermission = withPermission(({ children }) => children, ['license-delete']);
    const WithCreatePermission = withPermission(({ children }) => children, ['license-create']);

    useEffect(() => {
        if (!didFetchRef.current) {
            fetchLicenses(statusFilter, tenantId, currentPage);
            didFetchRef.current = true;
        }
    }, [statusFilter, tenantId, currentPage, licenses]);
    const fetchLicenses = async (status = '', page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/licenses`, {
                headers: { 'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,},
                params: { tenant_info_id: tenantId, status, page },
                skipLoader:true,
            });

            const licenseData = response.data.Data.map(license => {
                // Determine if the license is extended
                const isExtended = license.extension_type !== null;
                console.log(`License ID: ${license.id}, isExtended: ${isExtended}`);  // Debugging

                return {
                    ...license,
                    isExtended,
                };
            });

            setLicenses(licenseData);
            setPageCount(response.data.meta.total_pages);
        } catch (error) {
            console.error('Error fetching licenses:', error);
        }
    };

    const handleStatusChange = (event) => {
        const status = event.target.value;
        setStatusFilter(status);
        fetchLicenses(status, tenantId, currentPage);
    };

    const addLicense = async (newLicense) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const response = await axios.post(`${backendUrl}/api/licenses`, newLicense, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    tenant_info_id: tenantId, // Associate the new license with the tenant
                },
            });

            const createdLicense = response.data?.data;
            if (createdLicense) {
                createdLicense.setup_name = createdLicense.instance.setup_name;
                setLicenses((prevLicenses) => [...prevLicenses, createdLicense]);
            } else {
                console.error('Creation failed, no data returned.');
            }

            handleCloseModal();
        } catch (error) {
            if (error.response?.data?.errors) {
                return error.response.data.errors; // Return validation errors
            } else {
                console.error('Error adding license:', error);
            }
        }
    };

    const editLicense = async (licenseId, updatedData) => {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
          const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
          const token = userInfo?.token;
          const { extension_type, extension_days } = updatedData;
      
          // Only extend if extension type and value are provided
          if (extension_type && extension_days) {
            // Validate extension data (optional)
            if (
              !['days', 'weeks', 'months', 'years'].includes(extension_type) ||
              isNaN(extension_days) || extension_days < 1
            ) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: 'Invalid extension data.',
                    timer: 3000,
                    timerProgressBar: false,
                    showConfirmButton: false,
                    didOpen: () => {
                      const container = Swal.getContainer();
                      container.style.top = '50px';
                    },
                  });             
                   return;
            }
      
            // Add extension data to the updatedData object
            updatedData.extension_type = extension_type;
            updatedData.extension_days = extension_days;
          }
          
          const response = await axios.put(`${backendUrl}/api/licenses/${licenseId}`, updatedData, {
            headers: {
              'Authorization': `Bearer ${token}`,
                'Tenant-Origin': origin,
            },
            params: {
              tenant_info_id: tenantId, // Edit the license for this tenant
            },
          });
      
          const updatedLicense = response.data?.Data[0];
          if (updatedLicense) {
            updatedLicense.setup_name = updatedLicense.instance.setup_name;
      
            // Update isExtended flag based on extension data
            updatedLicense.isExtended = updatedLicense.extension_type !== null;
      
            setLicenses((prevLicenses) =>
              prevLicenses.map((license) =>
                license.id === updatedLicense.id ? updatedLicense : license
              )
            );
          } else {
            console.error('Update failed, no data returned.');
          }
      
          handleCloseModal();
        } catch (error) {
          console.error('Error updating license:', error);
        }
      };
      
    const deleteLicense = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            await axios.delete(`${backendUrl}/api/licenses/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                params: {
                    tenant_info_id: tenantId, // Delete the license for this tenant
                },
            });

            setLicenses(licenses.filter(license => license.id !== id));
            Swal.fire(
                "Deleted!",
                "The license has been deleted.",
                "success"
            );
        } catch (error) {
            console.error('Error deleting license:', error);
            Swal.fire(
                "Error!",
                "Failed to delete license. Please try again.",
                "error"
            );
        }
    }
    });
    
    };



    const handleExtendClick = (license) => {
        setSelectedLicenseData(license);

        setExtendType('days');
        setExtendValue(1);
        setShowExtendModal(true);
    };


    const handleLabelClick = (license) => {
        // If the license is extended, generate a message
        if (license.isExtended) {
            const extensionValue = license.extension_days || license.extension_days;
            const extensionType = license.extension_type;
            const validityDate = new Date(license.validity || license.validity); // Use extended validity if available

            // Construct the message based on the extension type and value
            let extensionText = '';
            switch (extensionType) {
                case 'days':
                    extensionText = `${extensionValue} day${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'weeks':
                    extensionText = `${extensionValue} week${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'months':
                    extensionText = `${extensionValue} month${extensionValue > 1 ? 's' : ''}`;
                    break;
                case 'years':
                    extensionText = `${extensionValue} year${extensionValue > 1 ? 's' : ''}`;
                    break;
                default:
                    extensionText = `${extensionValue} ${extensionType}`;
                    break;
            }

            // Set the message and show it
            setExtendedMessage(`License extended for ${extensionText} until: ${validityDate.toLocaleDateString()}`);
            setShowMessage(true);
        } else {
            // Show the "Not Extended" message
            setExtendedMessage('This license is not extended yet.');
            setShowMessage(true);
        }
    };

    useEffect(() => {
        if (showMessage) {
            const timeout = setTimeout(() => {
                setShowMessage(false);  // Hide the message after 3 seconds
            }, 3000);

            // Cleanup timeout if component unmounts or message disappears
            return () => clearTimeout(timeout);
        }
    }, [showMessage]); // Trigger this effect when showMessage changes



    // const handleSubmitExtend = () => {
    //     if (selectedLicenseData) {
    //         const updatedData = {
    //             extension_type: extendType,
    //             extension_days: extendValue,
    //         };

    //         // Call editLicense with the selected license's ID and updated extension data
    //         editLicense(selectedLicenseData.id, updatedData);
    //         setShowExtendModal(false); // Close modal after submission
    //     }
    // };

    const handleSubmitExtend = async () => {
        if (selectedLicenseData) {
            const updatedData = {
                extension_type: extendType,
                extension_days: extendValue,
            };
    
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;

                const userInfo = JSON.parse(localStorage.getItem('user-info'));
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;
                    const response = await axios.post(`${backendUrl}/api/licenses/${selectedLicenseData.id}/extend`, updatedData,{
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
    
                // Update the local state instead of refetching all data
                setLicenses((prevLicenses) =>
                    prevLicenses.map((license) =>
                        license.id === selectedLicenseData.id
                            ? { 
                                ...license, 
                                validity: response.data.new_validity_date, 
                                extended_validity: response.data.new_validity_date, 
                                isExtended: new Date(response.data.new_validity_date) > new Date(license.validity)
                              }
                            : license
                    )
                );
    
                // Close modal after submission
                setShowExtendModal(false);
            } catch (error) {
                console.error("Error extending license:", error);
            }
        }
    };
    

    const handleShowModal = (licenseId = null) => {
        if (licenseId) {
            const licenseToEdit = licenses.find((license) => license.id === licenseId);
            setSelectedLicenseData(licenseToEdit);
        } else {
            setSelectedLicenseData(null);
        }
        setSelectedLicenseId(licenseId);
        setShowModal(true);
    };


    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedLicenseId(null);
        setSelectedLicenseData(null);
        setErrors({});

    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchLicenses(statusFilter, tenantId, newPage); // Ensure tenant_info_id is passed with the page change
    };
    const handleViewDetails = (licenseId) => {
        navigate(`/license/details/${licenseId}`);
    };
      return (
        <>
            <div className="d-flex justify-content-end mb-3">
                <WithCreatePermission>
                    <button
                        type="button"
                        className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                        onClick={handleShowModal}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        id="tool"
                        title="Add License"
                    >
                        <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                    </button>
                </WithCreatePermission>
                <select
                    className="form-select form-select-sm ms-2 font-size-12"
                    value={statusFilter}
                    onChange={handleStatusChange}
                    style={{ width: '150px' }}
                >
                    <option value="all">All Licenses</option>
                    <option value="active">Active Licenses</option>
                    <option value="expired">Expired Licenses</option>
                    <option value="extended">Extended Licenses</option>
                </select>
            </div>

            {/* Horizontal Scroll Container */}
             <div className="contacts-table-modern">
                <div className="table-responsive">
                    <table className="table table-modern">
                        <thead>
                            
                        <tr>
                            <th scope="col" style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 11 }}>#</th>
                            <th scope="col">S_Admin</th>
                            <th scope="col">Admin</th>
                            <th scope='col'>Supervisor</th>
                            <th scope='col'>TeamLead</th>
                            <th scope='col'>Analyst</th>
                            <th scope='col'>CAgent</th>
                            <th scope='col'>VAgent</th>
                            <th scope='col'>TAgent</th>
                            <th scope='col'>Agent</th>
                            <th scope='col'>Email</th>
                            <th scope='col'>Voicemail</th>
                            <th scope='col'>Chat</th>
                            <th scope='col'>Interaction</th>
                            <th scope='col'>Voice</th>
                            <th scope='col'>Api</th>
                            <th scope='col'>Mobile App</th>
                            <th scope="col">Start Date</th>
                            <th scope="col">Validity</th>
                            <th scope="col">IsExtended</th>
                            <th scope="col" style={{ position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 11 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {licenses.map((license, index) => {
                            const validityDate = new Date(license.extended_validity || license.validity);
                            const today = new Date();
                            const isExpired = validityDate < today;
                            const isExtended = license.isExtended;

                            return (
                                <tr key={license.id}>
                                    <td style={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 9 }} className={isExpired ? 'expired-license' : ''}>
                                        {index + 1}
                                    </td>
                                    <td className={isExpired ? 'expired-license' : ''}>
                                        <Link to={`/license/details/${license.id}`} className="dropdown-item">{license.s_admin}</Link>
                                    </td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.admin}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.supervisor}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.team_lead}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.analyst}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.c_agent}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.v_agent}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.t_agent}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.agent}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.email}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.voicemail}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.chat}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.interaction}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.voice}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.api}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.mobile_app}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.start_date}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>{license.validity}</td>
                                    <td className={isExpired ? 'expired-license' : ''}>
                                        <span
                                            className={`badge ${isExtended ? 'badge-soft-success text-success' : 'badge-soft-danger text-danger'}`}
                                            onClick={() => handleLabelClick(license, index)}
                                            style={{ position: 'relative', cursor: 'pointer' }}
                                        >
                                            {isExtended ? 'Extended' : 'Not Extended'}
                                        </span>
                                        {showMessage && extendedMessage && (
                                            <div className="popup-message">
                                                {extendedMessage}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ position: 'sticky', right: 0, backgroundColor: 'white', zIndex: 9 }} className={isExpired ? 'expired-license' : ''}>
                                        <div className="dropdown">
                                            <i
                                                className="mdi mdi-dots-horizontal font-size-18"
                                                data-bs-toggle="dropdown"
                                                aria-expanded="false"
                                                style={{ cursor: 'pointer' }}
                                            ></i>
                                            <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleViewDetails(license.id)}
                                                    >
                                                        View Details
                                                    </button>
                                                </li>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => handleExtendClick(license)}
                                                    >
                                                        Extend Date
                                                    </button>
                                                </li>
                                                <li>
                                                    <WithEditPermission>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => handleShowModal(license.id)}
                                                        >
                                                            Edit
                                                        </button>
                                                    </WithEditPermission>
                                                </li>
                                                <li>
                                                    <WithDeletePermission>
                                                        <button
                                                            className="dropdown-item"
                                                            onClick={() => deleteLicense(license.id)}
                                                        >
                                                            Delete
                                                        </button>
                                                    </WithDeletePermission>
                                                </li>
                                            </ul>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            </div>

            {/* Rest of your existing code (Pagination, Modals, etc.) */}
            <div className="d-flex justify-content-end pagination-sm">
                <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={handlePageChange}
                    containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                    pageClassName={'page-item'}
                    pageLinkClassName={'page-link'}
                    previousClassName={'page-item previous'}
                    previousLinkClassName={'page-link'}
                    nextClassName={'page-item next'}
                    nextLinkClassName={'page-link'}
                    breakClassName={'page-item'}
                    breakLinkClassName={'page-link'}
                    activeClassName={'active'}
                />
            </div>

<Offcanvas show={showExtendModal} onHide={() => setShowExtendModal(false)} placement="end">
    <Offcanvas.Header closeButton>
        <Offcanvas.Title>Extend License</Offcanvas.Title>
    </Offcanvas.Header>
    <hr style={{ margin: 0 }} /><br/>

    <Offcanvas.Body>
        <Form>
            <Form.Group className="mb-3" controlId="extensionType">
                <Form.Label>Extend By:</Form.Label>
                <Form.Select
                    name="extension_type"
                    value={extendType}
                    onChange={(e) => setExtendType(e.target.value)}
                >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="extensionValue">
                <Form.Label>
                    Number of{' '}
                    {extendType === 'days'
                        ? 'Days'
                        : extendType === 'weeks'
                        ? 'Weeks'
                        : extendType === 'months'
                        ? 'Months'
                        : 'Years'}
                    :
                </Form.Label>
                <Form.Control
                    type="number"
                    min="1"
                    name="extension_days"
                    value={extendValue}
                    onChange={(e) => setExtendValue(parseInt(e.target.value, 10))}
                />
            </Form.Group>
            </Form>
            </Offcanvas.Body>
            <div className="p-3 d-flex justify-content-end border-top">
            <Button variant="primary" onClick={handleSubmitExtend} className='me-2'>
                    Submit
                </Button>
                <Button variant="secondary" onClick={() => setShowExtendModal(false)}>
                    Cancel
                </Button>
            </div>
       
</Offcanvas>


            <br />

            <div className="d-flex justify-content-end pagination-sm">
            <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />
            </div>

            {showModal && (
                <LicenseModal
                    showModal={showModal}
                    handleClose={handleCloseModal}
                    addLicense={addLicense}
                    editLicense={editLicense}
                    selectedLicenseId={selectedLicenseId}
                    selectedLicenseData={selectedLicenseData}
                    errors={errors}
                    setErrors={setErrors}
                // setSelectedInstanceId={setSelectedInstanceId}
                // setSelectedTenantId={setSelectedTenantId}

                />
            )}
        </>
    );
};

export default LicenseList;




